import {
  ApolloError,
  MutationFunctionOptions,
  MutationHookOptions,
  MutationResult,
  OperationVariables,
  SingleExecutionResult,
  useApolloClient,
  useMutation,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import {
  QUERY_PROGRESS_FAILED,
  QUERY_PROGRESS_NOT_STARTED,
  QUERY_PROGRESS_PENDING,
  QUERY_PROGRESS_SUCCEED,
} from "@recare/core/consts";
import { useSafeState } from "@recare/core/hooks";
import { AnyObject, QueryProgress, ValueOf } from "@recare/core/types";
import { useErrorHandler } from "apollo/mutations/utils";
import {
  TypedDocumentNode as DocumentNode,
  InputOf,
  ResultOf,
  TVariablesWithInput,
  VariablesOf,
} from "apollo/types";
import { logError } from "apollo/utils";
import { usePreventUnload } from "dsl/hooks";
import { useCallback, useMemo } from "react";
import { useApolloProgress } from "../utils";

export type GenericMutationResult<T, InputT = T> = [
  GenericMutationType<T, InputT>,
  QueryProgress,
  ResetProgressType,
  { [querySignature: string]: T },
  Error,
];

export type CustomMutationHook<
  DocumentType = any,
  ExtraProps extends object | void = void,
> = (
  props: ExtraProps extends object
    ? ExtraProps &
        Omit<Partial<CustomMutationHookOptions<DocumentType>>, keyof ExtraProps>
    : Partial<CustomMutationHookOptions<DocumentType>>,
) => CustomMutationHookResult<DocumentType>;

type CustomMutationHookOptions<DocumentType = any> = MutationHookOptions<
  ResultOf<DocumentType>,
  VariablesOf<DocumentType>
> &
  VariablesOf<DocumentType>;

type CustomMutationHookResult<DocumentType = any> = [
  TypedGenericMutationHookMutation<
    ResultOf<DocumentType>,
    VariablesOf<DocumentType>,
    InputOf<DocumentType>
  >,
  QueryProgress,
  (delay?: number) => void,
  ValueOf<ResultOf<DocumentType>>,
  MutationResult<ResultOf<DocumentType>>["error"],
];

type TypedGenericMutationHookMutation<
  TData = any,
  TVariables = OperationVariables,
  TInput = OperationVariables,
> = (
  input?: Partial<TInput>,
  options?: MutationFunctionOptions<
    TData,
    TVariablesWithInput<TVariables, TInput>
  >,
) => Promise<SingleExecutionResult<TData>>;

type TypedGenericMutationHookResult<
  TData = any,
  TVariables = OperationVariables,
  TInput = OperationVariables,
> = [
  TypedGenericMutationHookMutation<TData, TVariables, TInput>,
  QueryProgress,
  (delay?: number) => void,
  MutationResult<TData>["data"],
  MutationResult<TData>["error"],
];

export type ResetProgressType = () => void;

export type OperationResult<T> = Promise<{
  data: { [querySignature: string]: T };
}>;

export type MutationType<T> = (
  args: AnyObject & {
    input?: Partial<T>;
    variables?: AnyObject;
  },
) => OperationResult<T>;

export type GenericMutationType<T, InputT = T> = (
  input?: Partial<InputT>,
  extraVariables?: AnyObject,
) => OperationResult<T>;

export function getMutationProgress(
  loading: boolean,
  error: ApolloError | undefined,
  started: boolean,
) {
  if (error != null) {
    logError(error);
    return QUERY_PROGRESS_FAILED;
  }
  if (loading) return QUERY_PROGRESS_PENDING;
  return started ? QUERY_PROGRESS_SUCCEED : QUERY_PROGRESS_NOT_STARTED;
}

export function useMutationProgress<T>(
  mutate: MutationType<T>,
  { error, loading }: { error: any | undefined; loading: boolean },
): [MutationType<T>, QueryProgress, ResetProgressType] {
  const [started, setStarted] = useSafeState(false);

  const statefulMutation: MutationType<T> = useCallback(
    (args) => {
      setStarted(true);
      return mutate(args);
    },
    [mutate],
  );

  const queryProgress = useMemo(
    () => getMutationProgress(loading, error, started),
    [loading, started, error],
  );

  const resetProgress = useCallback(() => setStarted(false), [setStarted]);

  return [statefulMutation, queryProgress, resetProgress];
}

export function useTypedGenericMutation<
  TData = any,
  TVariables = OperationVariables,
  TInput = OperationVariables,
>(
  mutationFn: DocumentNode<TData, TVariables, TInput>,
  options?: MutationHookOptions<TData, TVariablesWithInput<TVariables, TInput>>,
): TypedGenericMutationHookResult<TData, TVariables, TInput> {
  const [mutation, result] = useMutation<
    TData,
    TVariablesWithInput<TVariables, TInput>
  >(mutationFn, options);

  const { progress, resetProgress } = useApolloProgress(result);

  usePreventUnload(progress);

  const modifiedMutation = useCallback(
    (
      input?: Partial<TInput>,
      options?: MutationFunctionOptions<
        TData,
        TVariablesWithInput<TVariables, TInput>
      >,
    ) =>
      mutation({
        ...options,
        variables: { ...options?.variables, input } as TVariablesWithInput<
          TVariables,
          TInput
        >,
      }),
    [mutation],
  );

  return [modifiedMutation, progress, resetProgress, result.data, result.error];
}

export function useGenericMutation<ReturnT, InputT = ReturnT>(
  gqlMutation: any,
  options: MutationHookOptions,
): [
  GenericMutationType<ReturnT, InputT>,
  QueryProgress,
  ResetProgressType,
  { [key: string]: ReturnT },
  ApolloError | undefined,
] {
  const [apolloMutation, { data, error, loading }] = useMutation(
    gqlMutation,
    options,
  );

  const [resettableMutation, mutationProgress, resetProgress] =
    useMutationProgress<ReturnT>(apolloMutation as MutationType<ReturnT>, {
      loading,
      error,
    });

  usePreventUnload(mutationProgress);

  const mutation = useCallback(
    (input: any, extraVariables?: AnyObject) =>
      resettableMutation({ variables: { input, ...extraVariables } }),
    [resettableMutation],
  );

  return [mutation, mutationProgress, resetProgress, data, error];
}

export const useRefetchOnConflict = () => {
  const errorHandler = useErrorHandler(onError);
  const client = useApolloClient();
  return useCallback(
    ({
      context,
      error,
      query,
      variables,
    }: {
      context?: AnyObject;
      error: AnyObject;
      query: DocumentNode;
      variables: AnyObject;
    }) => {
      if (error.message.includes("409")) {
        return client.query({
          query,
          variables,
          context,
          fetchPolicy: "network-only",
        });
      }
      return errorHandler(error);
    },
    [errorHandler, client],
  );
};
